import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  Pagination,
  LoadingState,
  EmptyState,
  ExportButton,
  Breadcrumbs,
  BreadcrumbItem,
} from '../components/ui';
import type { AuditLog } from '../types';
import { ShieldCheck, Filter, User, Clock, Terminal } from 'lucide-react';

export const AuditLogs: React.FC = () => {
  const getActionLabelPT = (action: string) => {
    const labels: Record<string, string> = {
      create: 'Criar',
      update: 'Atualizar',
      delete: 'Eliminar',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      suspend: 'Suspender',
    };
    return labels[action] || action;
  };

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'create':
      case 'approve':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50';
      case 'update':
        return 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-400 dark:border-sky-800/50';
      case 'delete':
      case 'reject':
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/50';
      case 'suspend':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  };

  const getResourceLabelPT = (resource: string) => {
    const labels: Record<string, string> = {
      client: 'Cliente',
      product: 'Produto',
      plan: 'Plano',
      integration_route: 'Rota de Integração',
      notification_template: 'Modelo de Notificação',
    };
    return labels[resource] || resource;
  };

  const [filters, setFilters] = useState({
    user_id: '',
    action_type: '',
    resource_type: '',
    date_from: '',
    date_to: '',
  });

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    setOffset(0);
  }, [filters, limit]);

  const { data, isLoading } = useQuery<{ data: AuditLog[]; pagination: { total: number } }>({
    queryKey: ['audit-logs', filters, offset, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await api.get<{ data: AuditLog[]; pagination: { total: number } }>(
        `/admin/audit-logs?${params.toString()}`
      );
      return response.data;
    },
  });

  const logs = data?.data || [];
  const pagination = data?.pagination;
  const total = pagination?.total || 0;

  const exportHeaders = [
    { key: 'created_at', label: 'Timestamp' },
    { key: 'user_email', label: 'Utilizador' },
    { key: 'action_type', label: 'Ação' },
    { key: 'resource_type', label: 'Recurso' },
    { key: 'resource_id', label: 'ID do Recurso' },
    { key: 'ip_address', label: 'Endereço IP' },
  ];

  const exportData = logs.map((log) => ({
    ...log,
    user_email: log.user?.email || 'Utilizador Desconhecido',
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Logs de Auditoria</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
            <ShieldCheck className="w-8 h-8 text-primary-500" />
            <span>Logs de Auditoria e Segurança</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
            Registro imutável de ações administrativas, aprovações e alterações de sistema
          </p>
        </div>
        <ExportButton
          data={exportData}
          filename={`audit_logs_${Date.now()}.csv`}
          headers={exportHeaders}
        />
      </div>

      {/* Filter Card */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold text-sm">
          <Filter className="w-4 h-4 text-primary-500" />
          <span>Filtros de Auditoria</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <input
            type="text"
            placeholder="Filtrar por ID do Utilizador..."
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden placeholder-slate-400"
            value={filters.user_id}
            onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
          />

          <select
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
            value={filters.action_type}
            onChange={(e) => setFilters({ ...filters, action_type: e.target.value })}
          >
            <option value="">Todas as Ações</option>
            <option value="create">Criar</option>
            <option value="update">Atualizar</option>
            <option value="delete">Eliminar</option>
            <option value="approve">Aprovar</option>
            <option value="reject">Rejeitar</option>
            <option value="suspend">Suspender</option>
          </select>

          <select
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
            value={filters.resource_type}
            onChange={(e) => setFilters({ ...filters, resource_type: e.target.value })}
          >
            <option value="">Todos os Recursos</option>
            <option value="client">Cliente</option>
            <option value="product">Produto</option>
            <option value="plan">Plano</option>
            <option value="integration_route">Rota de Integração</option>
            <option value="notification_template">Modelo de Notificação</option>
          </select>

          <div className="flex items-center gap-2">
            <span className="text-2xs font-semibold text-slate-400 dark:text-slate-500 uppercase">
              De:
            </span>
            <input
              type="date"
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-2xs font-semibold text-slate-400 dark:text-slate-500 uppercase">
              Até:
            </span>
            <input
              type="date"
              className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-3 py-2 text-xs focus:ring-2 focus:ring-primary-500 outline-hidden"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            />
          </div>
        </div>
      </div>

      {/* Logs Content */}
      {isLoading ? (
        <LoadingState type="card" count={3} />
      ) : logs.length > 0 ? (
        <>
          <div className="space-y-3">
            {logs.map((log) => (
              <div
                key={log.id}
                className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-5 hover:shadow-md transition-all group"
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2.5">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${getActionBadgeColor(
                          log.action_type
                        )}`}
                      >
                        {getActionLabelPT(log.action_type)}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 capitalize">
                        {getResourceLabelPT(log.resource_type)}
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(log.created_at).toLocaleString('pt-BR')}
                      </span>
                    </div>

                    <p className="text-sm text-slate-800 dark:text-slate-200 mb-1.5 leading-relaxed">
                      <span className="font-semibold text-slate-900 dark:text-white flex items-center gap-1 inline">
                        <User className="w-3.5 h-3.5 inline text-slate-400" />
                        {log.user?.email || 'Utilizador Desconhecido'}
                      </span>{' '}
                      efetuou a ação{' '}
                      <span className="font-semibold text-primary-600 dark:text-primary-400">
                        {getActionLabelPT(log.action_type).toLowerCase()}
                      </span>{' '}
                      no recurso{' '}
                      <span className="font-medium text-slate-900 dark:text-white">
                        {getResourceLabelPT(log.resource_type).toLowerCase()}
                      </span>{' '}
                      <span className="font-mono text-2xs text-slate-400 dark:text-slate-500">
                        (ID: {log.resource_id})
                      </span>
                    </p>

                    <p className="text-xs text-slate-400 dark:text-slate-500 font-mono flex items-center gap-1">
                      <Terminal className="w-3 h-3 text-slate-400" />
                      <span>IP: {log.ip_address}</span>
                    </p>

                    {(log.old_value || log.new_value) && (
                      <details className="mt-3 group/diff">
                        <summary className="text-xs font-semibold text-primary-600 dark:text-primary-400 cursor-pointer hover:underline focus:outline-hidden">
                          Ver alterações de dados
                        </summary>
                        <div className="mt-2.5 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          {log.old_value && (
                            <div className="rounded-xl border border-rose-200/60 dark:border-rose-900/40 bg-rose-50/30 dark:bg-rose-950/20 p-3">
                              <p className="font-semibold text-rose-700 dark:text-rose-400 mb-1.5 text-2xs uppercase tracking-wider">
                                Valor Anterior:
                              </p>
                              <pre className="bg-white/80 dark:bg-slate-950 p-2.5 rounded-lg overflow-x-auto border border-rose-200/50 dark:border-rose-900/40 font-mono text-2xs text-slate-800 dark:text-slate-200">
                                {JSON.stringify(log.old_value, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.new_value && (
                            <div className="rounded-xl border border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/30 dark:bg-emerald-950/20 p-3">
                              <p className="font-semibold text-emerald-700 dark:text-emerald-400 mb-1.5 text-2xs uppercase tracking-wider">
                                Novo Valor:
                              </p>
                              <pre className="bg-white/80 dark:bg-slate-950 p-2.5 rounded-lg overflow-x-auto border border-emerald-200/50 dark:border-emerald-900/40 font-mono text-2xs text-slate-800 dark:text-slate-200">
                                {JSON.stringify(log.new_value, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            onPageChange={setOffset}
            onLimitChange={setLimit}
          />
        </>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-8 text-center">
          <EmptyState
            title="Nenhum log de auditoria encontrado"
            description="Tente ajustar os seus filtros de pesquisa para visualizar outros registros."
          />
        </div>
      )}
    </div>
  );
};
